import {
  FacebookLoginClient,
  LoginResponse,
} from "@greatsumini/react-facebook-login";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Button, Form, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { isMobileOnly, isTablet } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import NafutelInputFormComponent from "src/components/forms/input";
import GoogleRecaptchaComponent from "src/components/google-recaptcha";
import {
  IconEmailComponent,
  IconFacebookComponent,
  IconLockComponent,
  IconTwitterComponent,
} from "src/components/images";
import LoadingComponent from "src/components/loading";
import { ButtonComponent } from "src/components/share";
import { EmailRegex, PassRegex } from "src/helper/consts";

import useAuth from "src/hooks/useAuth";
import { IStatusCode } from "src/interfaces";
import { QUERY_STRING_REDIRECT_URI } from "src/interfaces/defineQueryStringProperties";
import { AuthedServices, UnAuthServices } from "src/services";
import { getUrlParameter } from "src/utils/urlQueryHandle";
import BtnGoogleLoginComponent from "./google-login-btn";
import "./index.scss";

interface IProps {
  isRedirect?: boolean;
  closeLoginModal?: () => void;
}

function FormLoginComponent({ isRedirect, closeLoginModal }: IProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [tokenCaptra, setTokenCaptra] = useState<string | null>(null);
  const [isErrorCaptra, setErrorCaptra] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { setAuth, setShowPopupLogin }: any = useAuth();
  const [messageApi] = message.useMessage();
  const [isErrorLogin, setErrorLogin] = useState(false);
  // const AWS = require("aws-sdk");

  const redirect_uri_affter_logged =
    getUrlParameter(`${QUERY_STRING_REDIRECT_URI}`) || window.location.origin;
  const handleGetProfile = useCallback(
    async (user_type?: string) => {
      const response = await AuthedServices.getProfile({ param_datas: {} });
      if (response && response.data) {
        if (isRedirect) {
          window.location.href = `${redirect_uri_affter_logged}`;
        }
        setAuth((pre: any) => {
          return {
            ...pre,
            user: { ...response.data },
            user_type: user_type ?? "username_pass",
          };
        });
      }
    },
    [setAuth, isRedirect, redirect_uri_affter_logged]
  );

  const onFinish = (values: any) => {
    if (!tokenCaptra) {
      setErrorCaptra(true);
      return;
    }
    const dataReq = {
      param_datas: {
        auth_flow: "USER_PASSWORD_AUTH",
        ...values,
      },
    };
    setLoading(true);
    UnAuthServices.login(dataReq)
      .then((response) => {
        if (
          response &&
          response.data &&
          response.status_code === IStatusCode.OK
        ) {
          const acc = response.data?.authentication?.access_token;

          if (acc) {
            localStorage.setItem("access_token", acc);
            localStorage.setItem("user_type", "username_pass");
            form.resetFields();
            setAuth((pre: any) => {
              return { ...pre, access_token: acc, loading: false };
            });
            setShowPopupLogin(false);
            handleGetProfile();
          }
        }
      })
      .catch((err) => {
        if (err) {
          form.setFields([
            {
              name: "username",
              errors: [t("login.form.incorrect_info")],
            },
          ]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loginFb = () => {
    FacebookLoginClient.login(
      (res: LoginResponse) => {
        setLoading(true);
        if (res.authResponse?.accessToken) {
          FacebookLoginClient.getProfile(
            (resProfile: any) => {
              if (!resProfile?.email) {
                setLoading(false);
                setErrorLogin(true);
                return;
              }
              const dataReq = {
                param_datas: {
                  email: resProfile.email,
                  name: resProfile.name,
                  user_id: resProfile.id,
                  token: res.authResponse?.accessToken,
                },
              };
              UnAuthServices.loginWithFb(dataReq)
                .then((response) => {
                  if (
                    response &&
                    response.data &&
                    response.status_code === IStatusCode.OK
                  ) {
                    const acc = response.data?.authentication?.access_token;

                    if (acc) {
                      localStorage.setItem("access_token", acc);
                      localStorage.setItem("user_type", "facebook");
                      form.resetFields();
                      setAuth((pre: any) => {
                        return { ...pre, access_token: acc, loading: false };
                      });
                      setShowPopupLogin(false);
                      handleGetProfile("facebook");
                    }
                  }
                })
                .catch((err) => {
                  if (err) {
                    messageApi.error("ログインエラー");
                  }
                })
                .finally(() => {
                  setLoading(false);
                });
            },
            { fields: "name,email" }
          );
        } else {
          setLoading(false);
        }
      },
      {
        scope: "public_profile, email",
      }
    );
  };

  const loadFB = async () => {
    FacebookLoginClient.clear();
    await FacebookLoginClient.loadSdk("vi_VN");
    FacebookLoginClient.init({
      appId: process.env.REACT_APP_FB_APP_ID || "203809715584716",
      version: "v14.0",
    });
  };

  const loginGG = (accessToken: string) => {
    const dataReq = {
      param_datas: {
        token: accessToken,
      },
    };
    setLoading(true);
    UnAuthServices.loginWithGG(dataReq)
      .then((response) => {
        if (
          response &&
          response.data &&
          response.status_code === IStatusCode.OK
        ) {
          const acc = response.data?.authentication?.access_token;

          if (acc) {
            localStorage.setItem("access_token", acc);
            localStorage.setItem("user_type", "google");
            form.resetFields();
            setAuth((pre: any) => {
              return { ...pre, access_token: acc, loading: false };
            });
            setShowPopupLogin(false);
            handleGetProfile("google");
          }
        }
      })
      .catch((err) => {
        if (err) {
          messageApi.error("ログインエラー");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadFB();
  }, []);

  return (
    <>
      <LoadingComponent loading={loading}>
        {isErrorLogin ? (
          <div className="box-error-login">
            <div className="text-1">
              Facebookからメールアドレスを取得できませんでした
            </div>
            <div className="text-2">ログインして連携してください。</div>
            <div className="bottom">
              <ButtonComponent
                buttonLabel={t("common.btn.back")}
                varient="outline"
                style={{ width: 150 }}
                onClick={() => setErrorLogin(false)}
              />
            </div>
          </div>
        ) : (
          <div
            className={`form-login${isMobileOnly ? "-mobile" : ""}`}
            style={
              isTablet
                ? {
                    padding: "0 20px",
                    columnGap: 20,
                    width: "100%",
                  }
                : {}
            }
          >
            <div className="item-1">
              <Form
                form={form}
                name="form_login_hotel_revervation"
                onFinish={onFinish}
                autoComplete="off"
                requiredMark={false}
              >
                <NafutelInputFormComponent
                  name="username"
                  placeholder={t("login.form.username.placeholder")!}
                  iconLeft={<IconEmailComponent />}
                  rules={[
                    {
                      validator(rule, value, callback) {
                        if (!value) {
                          return callback(
                            t("reservation.input.error.common.required")!
                          );
                        }

                        const trimmedValue = value?.trim();
                        if (trimmedValue === "") {
                          callback(
                            t("reservation.input.error.common.required")!
                          );
                        } else if (!EmailRegex.test(trimmedValue)) {
                          callback(
                            t("forgotPassword.form.error.email.invalid")!
                          );
                        } else {
                          callback();
                        }
                      },
                    },
                  ]}
                  cssWrap={{ marginBottom: 10 }}
                />

                <NafutelInputFormComponent
                  cssWrap={{ marginTop: 16, marginBottom: 10 }}
                  name="password"
                  placeholder={t("login.form.password.placeholder")!}
                  iconLeft={<IconLockComponent />}
                  rules={[
                    {
                      required: true,
                      message: t("reservation.input.error.common.required")!,
                    },
                    {
                      pattern: new RegExp(PassRegex),
                      message: t("common.password.validate") || "",
                    },
                  ]}
                  type="password"
                />
                <div>
                  <GoogleRecaptchaComponent
                    callback={(t) => {
                      setTokenCaptra(t);
                      setErrorCaptra(false);
                    }}
                  />
                  {isErrorCaptra && (
                    <div style={{ color: "#ff4d4f", fontSize: 12 }}>
                      Please check captra
                    </div>
                  )}
                </div>
                <div>
                  <Button className="btn-submit" htmlType="submit">
                    {t("common.btn.login")}
                  </Button>
                </div>
                <div style={{ float: "right", marginTop: 8 }}>
                  {/* <a href="/auth/forgot-password" target={"_blank"} className="a-link">
                    {t("login.form.forgotPassword.link")}
                  </a> */}
                  <Link
                    onClick={() => {
                      closeLoginModal && closeLoginModal();
                    }}
                    className="a-link"
                    to={"/auth/forgot-password"}
                  >
                    {t("login.form.forgotPassword.link")}
                  </Link>
                </div>
              </Form>
            </div>

            <div className="item-2">
              <div className="wrap-login-social">
                <div
                  className="btn btn-facebook"
                  onClick={loginFb}
                  style={{ marginTop: 0 }}
                >
                  <IconFacebookComponent />
                  <div>{t("login.form.facebookButton")}</div>
                </div>

                <GoogleOAuthProvider clientId="226634783638-a34k5jvbnmtvqhg5u7p56b8e8pnjo0q2.apps.googleusercontent.com">
                  <BtnGoogleLoginComponent callback={loginGG} />
                </GoogleOAuthProvider>

                <div className="btn btn-twitter">
                  <IconTwitterComponent />
                  <div>{t("login.form.twitterButton")}</div>
                </div>

                <div
                  className="term"
                  style={isTablet ? { margin: 0, marginTop: 11 } : {}}
                >
                  {t("login.form.register.title")}
                  <br />
                  <a href="/auth/register" target={"_blank"} className="a-link">
                    {t("login.form.register.link")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </LoadingComponent>
    </>
  );
}

export default FormLoginComponent;
